import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import PageTitle from '../components/PageTitle';

const AdminPlayersPage = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [newPlayer, setNewPlayer] = useState({ name: '', torCode: '' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Mock data for initial development
  useEffect(() => {
    setPlayers([
      { id: 1, name: 'Juan Pérez', torCode: 'TOR001', createdAt: '2024-03-15' },
      { id: 2, name: 'María García', torCode: 'TOR002', createdAt: '2024-03-16' },
    ]);
  }, []);

  const handleBack = () => {
    navigate('/admin');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newPlayer.name || !newPlayer.torCode) {
      setError('Por favor completa todos los campos');
      return;
    }

    // Mock adding a new player
    const newPlayerData = {
      id: players.length + 1,
      ...newPlayer,
      createdAt: new Date().toISOString().split('T')[0]
    };

    setPlayers([...players, newPlayerData]);
    setNewPlayer({ name: '', torCode: '' });
    setSuccess('Jugador agregado exitosamente');
    setError(null);

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDelete = (id) => {
    setPlayers(players.filter(player => player.id !== id));
    setSuccess('Jugador eliminado exitosamente');
    setTimeout(() => setSuccess(null), 3000);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '1000px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header with back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton 
            onClick={handleBack}
            sx={{ 
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'primary.main', color: 'white' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <PageTitle title="Gestión de Jugadores" />
        </Box>

        {/* Form Section */}
        <SectionTitle title="Agregar Jugador">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Nombre del Jugador"
                  value={newPlayer.name}
                  onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Código TOR"
                  value={newPlayer.torCode}
                  onChange={(e) => setNewPlayer({ ...newPlayer, torCode: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  startIcon={<AddIcon />}
                  sx={{
                    bgcolor: '#c89e38',
                    '&:hover': { bgcolor: '#b38d2d' },
                    height: '40px'
                  }}
                >
                  Agregar
                </Button>
              </Grid>
            </Grid>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </SectionTitle>

        {/* Players List Section */}
        <SectionTitle 
          title="Jugadores Registrados" 
          subtitle={`Total: ${players.length} jugadores`}
        >
          <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Jugador</TableCell>
                  <TableCell>Código TOR</TableCell>
                  <TableCell>Fecha de Registro</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {players.map((player) => (
                  <TableRow key={player.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#c89e38' }}>
                          {player.name[0]}
                        </Avatar>
                        {player.name}
                      </Box>
                    </TableCell>
                    <TableCell>{player.torCode}</TableCell>
                    <TableCell>{player.createdAt}</TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(player.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionTitle>
      </Stack>
    </Box>
  );
};

export default AdminPlayersPage; 